import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import adminAuthService from '../../services/adminAuthService';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const AdminAuthenticationPortal = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated as admin
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate('/main-admin-control-center');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // Handle lockout
  useEffect(() => {
    if (failedAttempts >= 3) {
      setIsLocked(true);
      setError('Account locked due to multiple failed attempts. Please contact security.');
      
      // Auto-unlock after 5 minutes
      const timer = setTimeout(() => {
        setIsLocked(false);
        setFailedAttempts(0);
        setError('');
      }, 300000);

      return () => clearTimeout(timer);
    }
  }, [failedAttempts]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (isLocked) {
      setError('Account is locked. Please wait or contact security.');
      return;
    }

    if (!formData?.email || !formData?.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: authError } = await adminAuthService?.adminSignIn(
        formData?.email,
        formData?.password
      );

      if (authError) {
        setFailedAttempts(prev => prev + 1);
        setError(authError?.message || 'Invalid credentials or insufficient privileges');
        setLoading(false);
        return;
      }

      // Success - redirect to admin control center
      navigate('/main-admin-control-center');
    } catch (err) {
      setFailedAttempts(prev => prev + 1);
      setError('Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const password = formData?.password;
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;

    const levels = [
      { strength: 0, label: '', color: '' },
      { strength: 1, label: 'Weak', color: 'bg-red-500' },
      { strength: 2, label: 'Fair', color: 'bg-yellow-500' },
      { strength: 3, label: 'Good', color: 'bg-blue-500' },
      { strength: 4, label: 'Strong', color: 'bg-green-500' }
    ];

    return levels?.[strength];
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Security Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-600 to-orange-600 rounded-full mb-4 shadow-2xl">
            <Icon name="Shield" className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">BOLTPay Admin Portal</h1>
          <p className="text-gray-400 text-sm">Authorized Personnel Only</p>
        </div>

        {/* Login Card */}
        <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700 p-8">
          {/* Security Warning */}
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-200">
                <p className="font-semibold mb-1">Restricted Access</p>
                <p className="text-red-300/80">This portal is exclusively for BOLTPay administrators. Unauthorized access attempts are logged and monitored.</p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="XCircle" className="w-5 h-5 text-red-400" />
                <p className="text-red-200 text-sm">{error}</p>
              </div>
              {failedAttempts > 0 && failedAttempts < 3 && (
                <p className="text-red-300/70 text-xs mt-2">
                  Failed attempts: {failedAttempts}/3
                </p>
              )}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Admin Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Mail" className="w-5 h-5 text-gray-500" />
                </div>
                <Input
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="admin@boltpay.com"
                  disabled={isLocked || loading}
                  className="pl-10 bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 focus:border-red-500 focus:ring-red-500"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Admin Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Lock" className="w-5 h-5 text-gray-500" />
                </div>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Enter admin password"
                  disabled={isLocked || loading}
                  className="pl-10 pr-10 bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 focus:border-red-500 focus:ring-red-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLocked || loading}
                >
                  <Icon 
                    name={showPassword ? 'EyeOff' : 'Eye'} 
                    className="w-5 h-5 text-gray-500 hover:text-gray-400" 
                  />
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData?.password && passwordStrength?.strength > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${passwordStrength?.color} transition-all duration-300`}
                        style={{ width: `${(passwordStrength?.strength / 4) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400">{passwordStrength?.label}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLocked || loading}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Authenticating...</span>
                </div>
              ) : isLocked ? (
                <div className="flex items-center justify-center gap-2">
                  <Icon name="Lock" className="w-5 h-5" />
                  <span>Account Locked</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Icon name="LogIn" className="w-5 h-5" />
                  <span>Secure Admin Login</span>
                </div>
              )}
            </Button>
          </form>

          {/* Security Features */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-xs text-gray-500 text-center mb-4">Security Features Active</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-500" />
                <span>IP Verification</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-500" />
                <span>Device Fingerprint</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-500" />
                <span>Audit Logging</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-500" />
                <span>Auto Lockout</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/signin')}
            className="text-sm text-gray-400 hover:text-gray-300 transition-colors"
          >
            ← Return to User Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAuthenticationPortal;